import createClient, { type Middleware } from 'openapi-fetch'

import { authEvents } from '@/lib/auth-events'
import { fetchWithTimeout } from './request'
import type { components, paths } from './schema'

export type Schemas = components['schemas']

/** An API error. `message` is the stable code (e.g. SESSION_FULL) that src/lib/error-messages.ts translates. */
export class ApiError extends Error {
  readonly status: number

  constructor(code: string, status: number) {
    super(code)
    this.name = 'ApiError'
    this.status = status
  }
}

const errors: Middleware = {
  async onResponse({ response }) {
    if (response.ok) {
      return undefined
    }

    if (response.status === 401) {
      authEvents.setUser(null)
    }

    let code = `HTTP_${response.status}`
    try {
      const problem = (await response.clone().json()) as { code?: unknown }
      if (problem && typeof problem.code === 'string') {
        code = problem.code
      }
    } catch {
      // Not a problem details body; keep the status code.
    }

    throw new ApiError(code, response.status)
  },
}

/**
 * Typed client for the ASP.NET Core API (types generated from openapi/openapi.json via `npm run api:types`).
 * Same origin by default; in development Vite proxies /api to the backend.
 * Every non-2xx response is thrown as ApiError, so callers can rely on `data`.
 */
export const api = createClient<paths>({
  baseUrl: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '',
  credentials: 'include',
  fetch: (request) => fetchWithTimeout(request),
  // CSRF protection: the API rejects state-changing requests without this header.
  headers: { 'X-Requested-With': 'fetch' },
})

api.use(errors)

/** FormData body for multipart uploads (openapi-fetch would serialize JSON otherwise). */
export function formData(fields: Record<string, Blob>): FormData {
  const body = new FormData()
  for (const [name, value] of Object.entries(fields)) {
    body.append(name, value)
  }
  return body
}
